import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCreateUser, useUpdateUser } from '../../hooks/useUserMutations';
import { useExpertsList } from '../../hooks/useFileMakerData';
import { RoleSelector } from '../RoleSelector';
import { CustomerSearch } from '../CustomerSearch';
import { Button } from '@/ui/components/atoms/Button';
import type { User, CreateUserDto, FMCustomer, FMExpert } from '../../types';
import './UserForm.styles.css';

interface UserFormProps {
  user?: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({ user, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const { data: experts, isLoading: loadingExperts } = useExpertsList();
  
  const [formData, setFormData] = useState<CreateUserDto>({
    email: user?.email || '',
    password: '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    role: user?.role || 'bureau',
    fmName: user?.fmName || '',
    fm_client_id: user?.fm_client_id || null
  });
  
  const [selectedCustomer, setSelectedCustomer] = useState<FMCustomer | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load experts when role requires it
  useEffect(() => {
    if (['expert', 'bureau', 'admin'].includes(formData.role)) {
      // Experts will be loaded automatically by the hook
    }
  }, [formData.role]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate client/banque roles have customer selected
    if (['client', 'banque'].includes(formData.role) && !selectedCustomer) {
      setError(t('admin.userManagement.messages.selectCustomerRequired', 
        'Veuillez sélectionner un client FileMaker pour les rôles Client et Banque'));
      return;
    }

    try {
      const data = { ...formData };
      
      // Add fm_client_id for client/banque roles
      if (['client', 'banque'].includes(data.role) && selectedCustomer) {
        data.fm_client_id = selectedCustomer.fm_client_id;
      }
      
      // For bureau/admin/expert roles, set fm_client_id from the selected expert's id_user
      if (['bureau', 'admin', 'expert'].includes(data.role) && data.fmName) {
        const selectedExpert = experts?.find((ex: FMExpert) => ex.fm_name === data.fmName);
        if (selectedExpert && selectedExpert.id_user) {
          data.fm_client_id = selectedExpert.id_user;
        }
      }

      if (user) {
        await updateUser.mutateAsync({ id: user.id, data });
      } else {
        await createUser.mutateAsync(data);
      }
      
      onSuccess();
    } catch (error: any) {
      // Format error message
      let errorMessage = error.message || t('admin.userManagement.messages.error', 'Une erreur est survenue');
      
      if (error.errors && Array.isArray(error.errors)) {
        const fieldErrors = error.errors.map((err: any) => {
          if (typeof err === 'object' && err.field && err.message) {
            const cleanMessage = err.message.replace(/^["']|["']$/g, '');
            const fieldName = err.field.charAt(0).toUpperCase() + err.field.slice(1);
            return `${fieldName}: ${cleanMessage}`;
          }
          return typeof err === 'string' ? err : JSON.stringify(err);
        });
        errorMessage = `${errorMessage}\n\n${fieldErrors.join('\n')}`;
      }
      
      setError(errorMessage);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal__header">
          <h2 className="modal__title">
            {user ? 
              t('admin.userManagement.editUser', 'Modifier l\'utilisateur') : 
              t('admin.userManagement.createUser', 'Créer un utilisateur')
            }
          </h2>
          <button className="modal__close" onClick={onClose}>
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="modal__body">
          {error && (
            <div className="user-form__error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} id="user-form">
            <div className="form-group">
              <label>
                {t('admin.userManagement.form.role', 'Rôle')}
                <span className="user-form__required">*</span>
              </label>
              <RoleSelector
                value={formData.role}
                onChange={(role) => {
                  setFormData({ ...formData, role, fmName: '', fm_client_id: null });
                  setSelectedCustomer(null);
                }}
                disabled={!!user}
              />
            </div>

            {['client', 'banque'].includes(formData.role) && (
              <CustomerSearch
                value={selectedCustomer}
                onChange={setSelectedCustomer}
                onDataFill={(data) => {
                  setFormData({
                    ...formData,
                    firstName: data.first_name || formData.firstName,
                    lastName: data.last_name || formData.lastName,
                    email: data.email || formData.email,
                    phone: data.phone || formData.phone
                  });
                }}
              />
            )}

            {['expert', 'bureau', 'admin'].includes(formData.role) && (
              <div className="form-group">
                <label>
                  {t('admin.userManagement.form.fmUser', 'Utilisateur FileMaker')}
                  {formData.role === 'expert' && (
                    <span className="user-form__required">*</span>
                  )}
                </label>
                <select
                  value={formData.fmName}
                  onChange={(e) => {
                    const selectedExpert = experts?.find((ex: FMExpert) => ex.fm_name === e.target.value);
                    if (selectedExpert) {
                      setFormData({
                        ...formData,
                        fmName: e.target.value,
                        firstName: selectedExpert.prenom || formData.firstName,
                        lastName: selectedExpert.nom || formData.lastName,
                        email: selectedExpert.email || formData.email,
                        phone: selectedExpert.numero_gsm || formData.phone
                      });
                    } else {
                      setFormData({ ...formData, fmName: e.target.value });
                    }
                  }}
                  required={formData.role === 'expert'}
                  disabled={loadingExperts}
                >
                  <option value="">
                    {loadingExperts ?
                      t('admin.userManagement.messages.loading', 'Chargement...') :
                      t('admin.userManagement.form.selectExpert', 'Sélectionner pour pré-remplir')
                    }
                  </option>
                  {experts?.map((expert: FMExpert) => (
                    <option key={expert.fm_name} value={expert.fm_name}>
                      {expert.display_name} ({expert.fm_name})
                    </option>
                  ))}
                </select>
                <small className="text-gray-500">
                  {t('admin.userManagement.form.fmUserHint', 
                    'Note: fm_client_id sera défini avec l\'id_user de l\'expert sélectionné')}
                </small>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>
                  {t('admin.userManagement.form.firstName', 'Prénom')}
                  <span className="user-form__required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                  placeholder={t('admin.userManagement.form.firstNamePlaceholder', 'Entrez le prénom')}
                />
              </div>

              <div className="form-group">
                <label>
                  {t('admin.userManagement.form.lastName', 'Nom')}
                  <span className="user-form__required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                  placeholder={t('admin.userManagement.form.lastNamePlaceholder', 'Entrez le nom')}
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                {t('admin.userManagement.form.email', 'Email')}
                <span className="user-form__required">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder={t('admin.userManagement.form.emailPlaceholder', 'exemple@email.com')}
              />
            </div>

            {!user && (
              <div className="form-group">
                <label>
                  {t('admin.userManagement.form.password', 'Mot de passe')}
                  <span className="user-form__required">*</span>
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!user}
                  placeholder={t('admin.userManagement.form.passwordPlaceholder', 'Mot de passe sécurisé')}
                />
              </div>
            )}

            <div className="form-group">
              <label>
                {t('admin.userManagement.form.phone', 'Téléphone')}
                <span className="text-gray-500 text-sm ml-2">
                  ({t('admin.userManagement.form.optional', 'Optionnel')})
                </span>
              </label>
              <input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder={t('admin.userManagement.form.phonePlaceholder', '+33 1 23 45 67 89')}
              />
            </div>
          </form>
        </div>

        <div className="modal__footer">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            {t('admin.userManagement.actions.cancel', 'Annuler')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="user-form"
            loading={createUser.isPending || updateUser.isPending}
          >
            {user ? 
              t('admin.userManagement.actions.save', 'Enregistrer') : 
              t('admin.userManagement.actions.create', 'Créer')
            }
          </Button>
        </div>
      </div>
    </div>
  );
};